import {
	$getRoot,
	$isElementNode,
	LexicalNode,
	ElementNode,
	TextNode,
	LexicalEditor
} from "lexical";
import {
	$isListNode,
	$isListItemNode,
	ListNode,
	ListItemNode
} from "@lexical/list";
import {
	$isHeadingNode,
	$isQuoteNode,
	HeadingNode
} from "@lexical/rich-text";
import { $isCodeNode } from "@lexical/code";
import { $isLinkNode } from "@lexical/link";

/**
 * Style maps for inline styling
 */
const LIST_STYLES = {
	ul: "display: block; list-style-type: disc; margin-left: 0; margin-right: 0; padding-left: 40px;",
	ol: "display: block; list-style-type: decimal; margin-left: 0; margin-right: 0; padding-left: 40px;",
	li: "display: list-item; margin: 8px 0;"
};

const TEXT_STYLES = {
	p: "display: block; margin-top: 1em; margin-bottom: 1em;",
	h1: "display: block; font-size: 2em; margin-top: 0.67em; margin-bottom: 0.67em; font-weight: bold;",
	h2: "display: block; font-size: 1.5em; margin-top: 0.83em; margin-bottom: 0.83em; font-weight: bold;",
	h3: "display: block; font-size: 1.17em; margin-top: 1em; margin-bottom: 1em; font-weight: bold;",
	h4: "display: block; margin-top: 1.33em; margin-bottom: 1.33em; font-weight: bold;",
	h5: "display: block; font-size: 0.83em; margin-top: 1.67em; margin-bottom: 1.67em; font-weight: bold;",
	h6: "display: block; font-size: 0.67em; margin-top: 2.33em; margin-bottom: 2.33em; font-weight: bold;",
	blockquote: "border-left: 4px solid #ccc; margin: 16px 0; padding-left: 16px; color: #667;font-style: italic; display: block; padding-left: 10px;",
	pre: "display: block; font-family: monospace; white-space: pre; margin: 1em 0;",
	code: "font-family: monospace; background-color: #f0f0f0; padding: 2px 4px; border-radius: 3px;",
	a: "color: #0066cc; text-decoration: underline;"
};

const FORMAT_STYLES = {
	bold: "font-weight: bold;",
	italic: "font-style: italic;",
	underline: "text-decoration: underline;",
	strikethrough: "text-decoration: line-through;",
	code: "font-family: monospace; background-color: #f0f0f0; padding: 2px 4px; border-radius: 3px;"
};

/**
 * Generates HTML with inline styles from the Lexical editor's content
 * 
 * @param editor The LexicalEditor instance
 * @returns The HTML string representation with inline styles
 */
export function generateInlineStyledHtmlFromEditor(editor: LexicalEditor): string {
	return editor.getEditorState().read(() => {
		const root = $getRoot();
		
		// Process the root element
		const html = processNodeForExport(root);
		
		// Wrap in a div with basic styles if needed
		return `<div style="font-family: sans-serif; line-height: 1.5;">${html}</div>`;
	});
}

/**
 * Processes a Lexical node for HTML export with inline styles
 */
function processNodeForExport(node: LexicalNode): string {
	// Handle different node types
	if ($isListNode(node)) {
		return processListNode(node as ListNode);
	} else if ($isListItemNode(node)) {
		return processListItemNode(node as ListItemNode);
	} else if ($isElementNode(node)) {
		return processElementNode(node as ElementNode);
	} else if (node.getType() === "text") {
		return processTextNode(node as TextNode);
	}
	
	// For other node types
	return "";
}

/**
 * Processes a list node with inline styles
 */
function processListNode(node: ListNode): string {
	const tag = node.getTag();
	const style = LIST_STYLES[tag as keyof typeof LIST_STYLES] || "";
	
	const childrenHtml = node.getChildren()
		.map(child => processNodeForExport(child))
		.join("");
	
	return `<${tag} style="${style}">${childrenHtml}</${tag}>`;
}

/**
 * Processes a list item node with inline styles
 */
function processListItemNode(node: ListItemNode): string {
	const style = LIST_STYLES.li;
	
	// Process children, separating nested lists from other content
	let mainContent = "";
	let nestedLists = "";
	
	node.getChildren().forEach(child => {
		if ($isListNode(child)) {
			// Nested list
			nestedLists += processListNode(child as ListNode);
		} else {
			// Other content
			mainContent += processNodeForExport(child);
		}
	});
	
	// Combine main content and nested lists to ensure proper HTML structure
	return `<li style="${style}">${mainContent}${nestedLists}</li>`;
}

/**
 * Processes an element node with inline styles
 */
function processElementNode(node: ElementNode): string {
	// Special handling for different element types
	if ($isHeadingNode(node)) {
		const tag = (node as HeadingNode).getTag();
		const style = TEXT_STYLES[tag as keyof typeof TEXT_STYLES] || "";
		
		const childrenHtml = node.getChildren()
			.map(child => processNodeForExport(child))
			.join("");
			
		return `<${tag} style="${style}">${childrenHtml}</${tag}>`;
	} else if ($isQuoteNode(node)) {
		const style = TEXT_STYLES.blockquote;
		const childrenHtml = node.getChildren()
			.map(child => processNodeForExport(child))
			.join("");
			
		return `<blockquote style="${style}">${childrenHtml}</blockquote>`;
	} else if ($isCodeNode(node)) {
		const language = (node as any).getLanguage() || "";
		const style = TEXT_STYLES.pre;
		const childrenHtml = node.getChildren()
			.map(child => processNodeForExport(child))
			.join("");
			
		return `<pre style="${style}"><code${language ? ` data-language="${language}"` : ""}>${childrenHtml}</code></pre>`;
	} else if ($isLinkNode(node)) {
		const url = (node as any).getURL();
		const style = TEXT_STYLES.a;
		const childrenHtml = node.getChildren()
			.map(child => processNodeForExport(child))
			.join("");
			
		return `<a href="${url}" style="${style}">${childrenHtml}</a>`;
	} else {
		// Standard paragraph or other elements
		const tag = node.getType() === "paragraph" ? "p" : node.getType();
		const style = TEXT_STYLES[tag as keyof typeof TEXT_STYLES] || "";
		const childrenHtml = node.getChildren()
			.map(child => processNodeForExport(child))
			.join("");
		
		if (tag === "root") {
			return childrenHtml;
		}
		
		return `<${tag} style="${style}">${childrenHtml}</${tag}>`;
	}
}

/**
 * Processes a text node with inline styles for formatting
 */
function processTextNode(node: TextNode): string {
	let text = node.getTextContent();
	
	// HTML escape the text
	text = text
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#039;");
	
	// Keep track of applied styles to combine them
	const appliedStyles: string[] = [];
	
	// Check for each format type
	if (node.hasFormat("bold")) {
		appliedStyles.push(FORMAT_STYLES.bold);
	}
	if (node.hasFormat("italic")) {
		appliedStyles.push(FORMAT_STYLES.italic);
	}
	if (node.hasFormat("underline")) {
		appliedStyles.push(FORMAT_STYLES.underline);
	}
	if (node.hasFormat("strikethrough")) {
		appliedStyles.push(FORMAT_STYLES.strikethrough);
	}
	
	// If code format (overrides other formats)
	if (node.hasFormat("code")) {
		return `<code style="${FORMAT_STYLES.code}">${text}</code>`;
	}
	
	// If no formatting, return text as is
	if (appliedStyles.length === 0) {
		return text;
	}
	
	// Combine all styles
	const combinedStyle = appliedStyles.join(" ");
	return `<span style="${combinedStyle}">${text}</span>`;
}

/**
 * Ensures list numbering is correct by fixing any issues in the HTML
 */
export function fixListNumbering(html: string): string {
	// Only process if there might be list issues
	if (!html.includes("<ol") && !html.includes("<ul")) {
		return html;
	}

	// Use DOM parser to manipulate the HTML
	const parser = new DOMParser();
	const doc = parser.parseFromString(html, "text/html");
	
	// Find all ordered lists
	const orderedLists = doc.querySelectorAll("ol");
	
	orderedLists.forEach(ol => {
		// Fix any issues with list items
		const listItems = ol.querySelectorAll("li");
		
		listItems.forEach(li => {
			// Check for text content that might include numbers
			const text = li.textContent || "";
			
			// Look for patterns like "1. Text" at the start
			const numMatch = text.match(/^\s*(\d+)\.\s+/);
			if (numMatch) {
				// Remove the redundant number prefix
				const cleanText = text.replace(/^\s*\d+\.\s+/, "");
				li.textContent = cleanText;
			}
		});
	});
	
	// Return the fixed HTML
	return doc.body.innerHTML;
}

/**
 * Generate HTML with proper inline styles and fixed list structure
 */
export function generateExportHtmlWithInlineStyles(editor: LexicalEditor): string {
	// Generate HTML with inline styles
	const styledHtml = generateInlineStyledHtmlFromEditor(editor);
	
	// Fix any list numbering issues
	return fixListNumbering(styledHtml);
}